import { useTranslation } from 'react-i18next';
import {
  ANNOUNCEMENT_FONTS as F,
  ANNOUNCEMENT_PALETTE as P,
  announcementButtonBase,
  formatAnnouncementDate,
  type AnnouncementCardProps,
} from '../announcement-style';

const STYLES = `
@keyframes announcementNoteIn {
  from { opacity: 0; transform: translateY(14px) rotate(-1.1deg); }
  to { opacity: 1; transform: translateY(0) rotate(-1.1deg); }
}
.announcement-note { padding: 34px 28px 26px; transform: rotate(-1.1deg); }
.announcement-note h2 { font-size: clamp(1.5rem, 5.6vw, 2.1rem); padding-right: 72px; }
.announcement-note .announcement-note-kicker { padding-right: 84px; }
.announcement-note .announcement-note-stamp { width: 74px; height: 74px; font-size: 14px; top: 14px; right: 16px; }
@container announcement (max-width: 700px) {
  .announcement-note { padding: 28px 20px 22px; transform: rotate(-.6deg); }
  .announcement-note h2 { padding-right: 58px; }
  .announcement-note .announcement-note-kicker { padding-right: 64px; }
  .announcement-note .announcement-note-stamp { width: 62px; height: 62px; font-size: 12px; top: 10px; right: 10px; }
}
`;

const tapeStyle: React.CSSProperties = {
  position: 'absolute',
  width: 96,
  height: 24,
  background: 'rgba(248,215,221,.82)',
  boxShadow: '0 1px 2px rgba(26,16,24,.12)',
  pointerEvents: 'none',
};

/**
 * "Recado no mural" — a paper note taped to the wall: tape on both top
 * corners, wheat-paste drips, a round rotated "Novo" stamp with the date,
 * condensed headline and serif body. The closest relative of the classic
 * card, in the world of the journey/mural posters.
 */
export function WallNoteCard(props: AnnouncementCardProps) {
  const { t } = useTranslation('p2e');

  return (
    <article
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="announcement-note"
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        color: P.darkText,
        borderRadius: 2,
        backgroundColor: P.paper,
        backgroundImage: 'radial-gradient(rgba(26,16,24,.045) 1px, transparent 1.4px)',
        backgroundSize: '7px 7px',
        boxShadow: '8px 14px 36px rgba(0,0,0,.4), 0 0 0 1px rgba(26,16,24,.06) inset',
        animation: props.reducedMotion ? undefined : 'announcementNoteIn .35s ease both',
      }}
    >
      <style>{STYLES}</style>

      <div aria-hidden="true" style={{ ...tapeStyle, top: -10, left: -22, transform: 'rotate(-32deg)' }} />
      <div aria-hidden="true" style={{ ...tapeStyle, top: -10, right: -22, transform: 'rotate(32deg)' }} />

      <div
        aria-hidden="true"
        className="announcement-note-stamp"
        style={{
          position: 'absolute',
          borderRadius: '50%',
          border: `3px double ${P.maxRed}`,
          color: P.maxRed,
          display: 'grid',
          placeItems: 'center',
          fontFamily: F.cond,
          fontWeight: 700,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          transform: 'rotate(12deg)',
          background: 'rgba(255,248,245,.6)',
          lineHeight: 1,
          textAlign: 'center',
        }}
      >
        <span>
          {t('announcements.badgeNew')}
          <small style={{ display: 'block', fontSize: 8, letterSpacing: '.22em', marginTop: 3, fontWeight: 600 }}>
            {formatAnnouncementDate(props.date, props.locale, 'short')}
          </small>
        </span>
      </div>

      <p
        className="announcement-note-kicker"
        style={{
          fontFamily: F.cond,
          fontSize: 11,
          letterSpacing: '.28em',
          textTransform: 'uppercase',
          color: P.maxRed,
          fontWeight: 600,
          margin: '0 0 11px',
        }}
      >
        {t('announcements.kicker')}
      </p>

      <h2
        style={{
          fontFamily: F.cond,
          textTransform: 'uppercase',
          fontWeight: 700,
          lineHeight: 1.02,
          letterSpacing: '.01em',
          margin: '0 0 14px',
          textWrap: 'balance',
        }}
      >
        {props.title}
      </h2>

      {props.body && (
        <p
          style={{
            fontFamily: F.serif,
            fontSize: 16,
            lineHeight: 1.65,
            color: P.midText,
            margin: '0 0 18px',
            whiteSpace: 'pre-line',
          }}
        >
          {props.body}
        </p>
      )}

      {props.media && <div style={{ marginBottom: 18 }}>{props.media}</div>}

      <hr style={{ border: 0, borderTop: '1px dashed rgba(26,16,24,.25)', margin: '0 0 17px' }} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button
          ref={props.primaryRef}
          type="button"
          disabled={props.primaryDisabled}
          onClick={props.onPrimary}
          style={{
            ...announcementButtonBase,
            background: P.maxRed,
            color: P.white,
            opacity: props.primaryDisabled ? 0.45 : 1,
            cursor: props.primaryDisabled ? 'default' : 'pointer',
          }}
        >
          {props.primaryLabel}
        </button>
        {props.skippable && (
          <button
            type="button"
            onClick={props.onSkip}
            style={{
              ...announcementButtonBase,
              background: 'transparent',
              color: P.warmGray,
              borderColor: P.lightBorder,
            }}
          >
            {props.skipLabel}
          </button>
        )}
      </div>

      {props.gatedHint && (
        <p style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 13, color: P.midText, margin: '10px 0 0' }}>
          {props.gatedHint}
        </p>
      )}

      {/* wheat-paste drips */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -9, left: '18%', width: 16, height: 22, borderRadius: '0 0 10px 10px', background: 'rgba(248,215,221,.9)' }}
      />
      <div
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -5, left: '23%', width: 9, height: 12, borderRadius: '0 0 10px 10px', background: 'rgba(248,215,221,.9)' }}
      />
    </article>
  );
}
